import React, { useMemo, useRef, useState } from 'react';
import { styled } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { ACCENT_BLUE } from './qcTheme';

// Curated vocabulary: `value` persists on the AssessorAnswer (AssessorFindingTagChoices), `label` is display-only; fixed because tags are a filter dimension.
export const CURATED_TAGS: { value: string; label: string; text: string; rgb: string }[] = [
  { value: 'follow_up', label: 'Follow-up', text: '#512da8', rgb: '94,53,177' },
  { value: 'needs_evidence', label: 'Needs evidence', text: '#a86b00', rgb: '255,171,0' },
  { value: 'compensating_control', label: 'Compensating control', text: '#006d77', rgb: '0,109,119' },
  { value: 'discuss_with_company', label: 'Discuss w/ company', text: '#ad1457', rgb: '194,24,91' },
];

const FALLBACK_THEME = { text: '#455a64', rgb: '84,110,122' };

export function tagTheme(value: string): { text: string; rgb: string } {
  return CURATED_TAGS.find(t => t.value === value) ?? FALLBACK_THEME;
}

// Unknown values predate the fixed vocabulary — the raw value beats showing nothing.
export function tagLabel(value: string): string {
  return CURATED_TAGS.find(t => t.value === value)?.label ?? value;
}

const TagsRow = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  flexWrap: 'wrap',
});

const TagChip = styled('span', {
  shouldForwardProp: prop => !['rgb', 'tcolor'].includes(prop as string),
})<{ rgb: string; tcolor: string }>(({ rgb, tcolor }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  background: `rgba(${rgb},0.12)`,
  color: tcolor,
  border: `1px solid rgba(${rgb},0.6)`,
  height: '28px',
  padding: '0 12px',
  borderRadius: '14px',
  fontSize: '0.78rem',
  fontWeight: 600,
  '& .MuiSvgIcon-root': {
    fontSize: '15px',
  },
}));

const RemoveTag = styled('button')({
  display: 'inline-flex',
  border: 'none',
  background: 'none',
  padding: 0,
  cursor: 'pointer',
  color: 'inherit',
  opacity: 0.7,
});

const AddTagButton = styled('button')({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '4px',
  background: '#fff',
  color: ACCENT_BLUE,
  border: '1px dashed rgba(25,118,210,0.6)',
  height: '28px',
  padding: '0 12px',
  borderRadius: '14px',
  fontSize: '0.78rem',
  fontWeight: 500,
  cursor: 'pointer',
  fontFamily: 'inherit',
  '&:hover': {
    background: 'rgba(25,118,210,0.06)',
    borderColor: ACCENT_BLUE,
  },
  '& .MuiSvgIcon-root': {
    fontSize: '15px',
  },
});

const InputWrap = styled('div')({
  position: 'relative',
  display: 'inline-flex',
});

// Own width, not the wrap's edges — the anchor is only the small + Tag button.
const Dropdown = styled('div')({
  position: 'absolute',
  top: '36px',
  left: 0,
  minWidth: '200px',
  background: '#fff',
  borderRadius: '6px',
  boxShadow: '0 5px 5px -3px rgba(0,0,0,0.2),0 8px 10px 1px rgba(0,0,0,0.14),0 3px 14px 2px rgba(0,0,0,0.12)',
  zIndex: 20,
  padding: '4px 0',
  maxHeight: '220px',
  overflow: 'auto',
});

const DropdownRow = styled('button')({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  width: '100%',
  border: 'none',
  background: 'none',
  textAlign: 'left',
  padding: '8px 12px',
  fontSize: '0.82rem',
  whiteSpace: 'nowrap',
  color: 'rgba(0,0,0,0.8)',
  cursor: 'pointer',
  fontFamily: 'inherit',
  '&:hover': {
    background: '#f3f5fb',
  },
});

const TagDot = styled('span', {
  shouldForwardProp: prop => prop !== 'dot',
})<{ dot: string }>(({ dot }) => ({
  width: '8px',
  height: '8px',
  borderRadius: '50%',
  background: dot,
  flex: 'none',
}));

export interface TagInputProps {
  tags: string[];
  editable: boolean;
  onAdd: (tag: string) => void;
  onRemove: (tag: string) => void;
}

export default function TagInput({ tags, editable, onAdd, onRemove }: TagInputProps): JSX.Element {
  const [adding, setAdding] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  const available = useMemo(
    () => CURATED_TAGS.filter(tag => !tags.includes(tag.value)),
    [tags],
  );

  return (
    <TagsRow>
      {tags.map(tag => {
        const theme = tagTheme(tag);
        return (
          <TagChip key={tag} rgb={theme.rgb} tcolor={theme.text}>
            <CheckIcon />
            {tagLabel(tag)}
            {editable && (
              <RemoveTag
                type='button'
                aria-label={`Remove ${tagLabel(tag)}`}
                onClick={() => onRemove(tag)}
              >
                <CloseIcon sx={{ fontSize: 15 }} />
              </RemoveTag>
            )}
          </TagChip>
        );
      })}
      {editable && !adding && available.length > 0 && (
        <AddTagButton type='button' onClick={() => setAdding(true)}>
          <AddIcon />
          Tag
        </AddTagButton>
      )}
      {editable && adding && (
        <InputWrap ref={wrapRef} onBlur={() => setTimeout(() => setAdding(false), 150)}>
          <AddTagButton type='button' onClick={() => setAdding(false)}>
            <AddIcon />
            Tag
          </AddTagButton>
          <Dropdown>
            {available.map(tag => (
              <DropdownRow
                key={tag.value}
                type='button'
                onMouseDown={() => { onAdd(tag.value); setAdding(false); }}
              >
                <TagDot dot={tag.text} />
                {tag.label}
              </DropdownRow>
            ))}
          </Dropdown>
        </InputWrap>
      )}
    </TagsRow>
  );
}
